<template>
  <div class="flex flex-col h-full">
    <Teleport to="#topbar-search" defer>
      <SearchInput
        v-model="applicationsStore.searchQuery"
        placeholder="Search applications..."
        :debounce-time="500"
        @search="handleSearch"
        class="w-full"
      />
    </Teleport>
    <DashboardHeader
      :title="`My Applications (${applicationsStore?.userTotalCount} found)`"
      link="/applications"
    >
      <template #right>
        <router-link
            v-if="!isLimitedUser"
            to="/jobs/create-new"
            class="text-xs md:text-xs w-fit font-normal bg-cosmicPurple text-white px-2 pr-4 md:px-4 md:pl-2 py-2 rounded-md flex items-center hover:bg-cosmicPurple/80 transition-all duration-200 whitespace-nowrap"
          >
            <Plus class="inline-block h-4 mr-1" />
            <span class="hidden lg:block">Create new job posting</span>
            <span class="block lg:hidden">New</span>
          </router-link>
        <div class="flex items-center gap-3">
          <ViewToggle 
            :current-view="currentView" 
            @update:view="updateView"
          />
          <router-link
            v-if="isLimitedUser"
            to="/applications/create-new"
            class="text-xs md:text-xs font-normal bg-cosmicPurple text-white px-2 pr-4 md:px-4 md:pl-2 py-2 rounded-md flex items-center hover:bg-cosmicPurple/80 transition-all duration-200 whitespace-nowrap"
          >
            <Plus class="inline-block h-4 mr-1" />
            <span class="hidden md:block">Create new job application</span>
            <span class="block md:hidden">New</span>
          </router-link>
        </div>
      </template>
    </DashboardHeader>
    <GoalsReminderCard v-if="isFellow" />
    <div class="mb-2 flex flex-wrap items-center gap-2">
      <ApplicationStatusFilters 
        :show-application-status="true"
        :show-referral-status="!isLimitedUser"
        :show-referral-type="!isLimitedUser"
        :show-interview-status="true"
        :model-value="filterValues"
        @filter-change="handleFilterChange"
        @clear-all="handleClearAll"
      />
      
      <Combobox v-model="selectedEmployer">
        <ComboboxAnchor as-child>
          <ComboboxTrigger as-child>
            <button
              class="inline-flex items-center gap-1.5 h-8 rounded-full px-3 text-xs font-medium border bg-white hover:bg-gray-50 transition-colors"
              :class="selectedEmployer && selectedEmployer.id !== 'all'
                ? 'border-cosmicPurple/40 bg-cosmicPurple/5 text-cosmicPurple'
                : 'border-gray-200 text-gray-600'"
            >
              <Building2 class="w-3 h-3 shrink-0" />
              {{ selectedEmployer && selectedEmployer.id !== 'all' ? selectedEmployer.name : 'Employer' }}
              <ChevronDown class="w-3 h-3 opacity-40" />
            </button>
          </ComboboxTrigger>
        </ComboboxAnchor>

        <ComboboxList ref="employerOptionsContainer" class="max-h-[200px] overflow-y-auto" @scroll="handleEmployerScroll">
          <div class="sticky top-0 z-10 bg-popover w-full items-center">
            <ComboboxInput 
              v-model="employerSearch"
              class="pl-9 focus-visible:ring-0 border-0 border-b rounded-none h-10" 
              placeholder="Search employers..." 
            />
            <span class="absolute start-0 inset-y-0 flex items-center justify-center px-3">
              <Search class="size-4 text-muted-foreground" />
            </span>
          </div>

          <ComboboxEmpty>
            No employers found.
          </ComboboxEmpty>

          <ComboboxGroup>
            <div v-if="employerStore.isLoading" class="flex justify-center py-2">
              <div class="animate-spin rounded-full h-4 w-4 border-2 border-primary"></div>
            </div>
            <ComboboxItem
              v-for="employer in filteredEmployers"
              :key="employer.id"
              :value="employer"
            >
              {{ employer.name }}
              <ComboboxItemIndicator>
                <Check class="ml-auto h-4 w-4" />
              </ComboboxItemIndicator>
            </ComboboxItem>
            <div v-if="employerStore.nextPage && !employerStore.isLoading" class="text-center text-sm text-gray-500 py-2">
              Scroll for more...
            </div>
          </ComboboxGroup>
        </ComboboxList>
      </Combobox>
    </div>

    <!-- Main content section - will scroll -->
    <div
      class="flex w-full flex-1 overflow-hidden border-[1px] border-gray-200 rounded-xl bg-white transition-all duration-200 fade-in"
    >
      <!-- KeepAlive requires exactly one child, so we use a dynamic component -->
      <KeepAlive>
        <component
          :is="activeViewComponent"
          :key="currentView"
          v-bind="activeViewProps"
          :class="activeViewClass"
        />
      </KeepAlive>

      <!-- Application details section (shared across all views) -->
      <router-view
        v-if="route.params.application_id"
        :application-id="route.params.application_id"
        class="w-full flex-[5]"
        @refresh-requested="handleRefreshRequested"
      />
    </div>

  </div>
</template>

<script setup lang="ts">
import { ref, onMounted, computed, watch, nextTick } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { useApplicationsStore } from '@/stores/applications'
import { useEmployerStore } from '@/stores/employer'
import { Plus, ChevronDown, Search, Building2 } from 'lucide-vue-next'
import UserApplicationsList from '@/components/userApplications/UserApplicationsList.vue'
import JobApplicationsTable from '@/components/common/jobApplication/JobApplicationsTable.vue'
import ApplicationsKanban from '@/components/userApplications/ApplicationsKanban.vue'
import ApplicationStatusFilters from '@/components/common/ApplicationStatusFilters.vue'
import ViewToggle from '@/components/common/ViewToggle.vue'
import type { Component } from 'vue'
import { useUserStore } from '@/stores/user'
import { useAuth } from '@/composables/useAuth'
import DashboardHeader from '@/components/common/DashboardHeader.vue'
import GoalsReminderCard from '@/components/jobs/GoalsReminderCard.vue'
import SearchInput from '@/components/common/SearchInput.vue'
import {
  Combobox,
  ComboboxAnchor,
  ComboboxEmpty,
  ComboboxGroup,
  ComboboxInput,
  ComboboxItem,
  ComboboxItemIndicator,
  ComboboxList,
  ComboboxTrigger
} from '@/components/ui/combobox'
import { Check } from 'lucide-vue-next'

const applicationsStore = useApplicationsStore()
const employerStore = useEmployerStore()
const route = useRoute()
const router = useRouter()
const userStore = useUserStore()
const { isLimitedUser, isFellow } = useAuth()
const currentView = ref<'list' | 'table' | 'kanban'>('table')

// Create the 'All employers' option
const allEmployersOption = { id: 'all', name: 'All employers' }

// Employer filtering state
const selectedEmployer = ref<any>(allEmployersOption)
const employerSearch = ref('')
const employerOptionsContainer = ref<HTMLElement | null>(null)

// Computed filtered employers with 'All employers' option at the top
const filteredEmployers = computed(() => {
  const sourceEmployers = employerStore.employerOptions.length > 0 ? employerStore.employerOptions : employerStore.employers
  const employers = !employerSearch.value ? sourceEmployers : sourceEmployers
  return [allEmployersOption, ...employers]
})

const userId = computed(() => {
  return userStore.currentUser?.id?.toString()
})

// ─── Dynamic view component (required for <KeepAlive> single-child constraint) ─

const activeViewComponent = computed<Component>(() => {
  if (currentView.value === 'list') return UserApplicationsList
  if (currentView.value === 'kanban') return ApplicationsKanban
  return JobApplicationsTable
})

const activeViewProps = computed<Record<string, unknown>>(() => {
  if (currentView.value === 'table') {
    return {
      keyProp: route.params.application_id,
      userId: userId.value,
      currentPage: currentPage.value,
      totalPages: totalPages.value
    }
  }
  if (currentView.value === 'list') {
    return { keyProp: route.params.application_id }
  }
  return {}
})

const activeViewClass = computed(() => {
  if (currentView.value === 'kanban') {
    return [
      'flex-[3] transition-all duration-200 fade-in',
      {
        'hidden xl:block': route.params.application_id,
        'opacity-50': applicationsStore.isLoadingKanban
      }
    ]
  }
  return [
    'flex-[3] transition-all duration-200 fade-in',
    {
      'hidden xl:block': route.params.application_id,
      'opacity-50': applicationsStore.isLoadingList
    }
  ]
})

const currentPage = computed(() => {
  return route.query.page ? parseInt(route.query.page as string) : 1
})

const totalPages = computed(() => {
  return Math.ceil(applicationsStore.userTotalCount / applicationsStore.pageSize)
})

const handleSearch = () => {
  // eslint-disable-next-line @typescript-eslint/no-floating-promises
  updateUrlAndFetch()
}

const updateView = (view: 'list' | 'table' | 'kanban') => {
  currentView.value = view
  localStorage.setItem('applicationsViewMode', view)
  if (view === 'kanban') {
    // eslint-disable-next-line @typescript-eslint/no-floating-promises
    applicationsStore.fetchApplicationsForKanban()
  }
}

const filterValues = computed(() => ({
  applicationStatus: applicationsStore.selectedApplicationStatus || '',
  referralStatus: applicationsStore.selectedReferralStatus || '',
  referralType: applicationsStore.selectedReferralType || '',
  interviewStatus: applicationsStore.selectedInterviewStatus || ''
}))


const handleEmployerScroll = async (event: Event) => {
  const container = event.target as HTMLElement
  const { scrollTop, scrollHeight, clientHeight } = container
  
  if (scrollHeight - (scrollTop + clientHeight) < 50 && !employerStore.isLoading && employerStore.nextPage) {
    try {
      await employerStore.loadMoreEmployers()
    } catch (error) {
      console.error('Failed to load more employers:', error)
    }
  }
}

// Watch for employer search changes
let employerSearchTimeout: number | null = null
watch(employerSearch, (newSearch) => {
  if (employerSearchTimeout) {
    clearTimeout(employerSearchTimeout)
  }
  
  employerSearchTimeout = setTimeout(async () => {
    if (newSearch.trim()) {
      await employerStore.searchEmployers(newSearch.trim())
    } else {
      await employerStore.searchEmployers('')
    }
  }, 300) as unknown as number
})

// Watch for search query changes with debounce
let searchTimeout: number | null = null
watch(() => applicationsStore.searchQuery, () => {
  if (searchTimeout) {
    clearTimeout(searchTimeout)
  }
  
  searchTimeout = setTimeout(() => {
    // eslint-disable-next-line @typescript-eslint/no-floating-promises
    updateUrlAndFetch()
  }, 500) as unknown as number
})

const handleFilterChange = (filters: { applicationStatus?: string; referralStatus?: string; referralType?: string; interviewStatus?: string }) => {
  applicationsStore.selectedApplicationStatus = filters.applicationStatus || ''
  applicationsStore.selectedReferralStatus = filters.referralStatus || ''
  applicationsStore.selectedReferralType = filters.referralType || ''
  applicationsStore.selectedInterviewStatus = filters.interviewStatus || ''
  
  // Update URL with filter parameters
  // eslint-disable-next-line @typescript-eslint/no-floating-promises
  updateUrlAndFetch()
}

const handleClearAll = () => {
  // Also clear the employer filter when Clear All is clicked
  selectedEmployer.value = allEmployersOption
  // eslint-disable-next-line @typescript-eslint/no-floating-promises
  updateUrlAndFetch()
}

const updateUrlAndFetch = async () => {
  const query: any = {}

  // Add search parameter
  if (applicationsStore.searchQuery && applicationsStore.searchQuery.trim() !== '') {
    query.search = applicationsStore.searchQuery.trim()
  }

  // Add filter parameters if they're not empty
  if (applicationsStore.selectedApplicationStatus && applicationsStore.selectedApplicationStatus !== '') {
    query.application_status = applicationsStore.selectedApplicationStatus
  }
  if (applicationsStore.selectedReferralStatus && applicationsStore.selectedReferralStatus !== '') {
    query.referral_status = applicationsStore.selectedReferralStatus
  }
  if (applicationsStore.selectedReferralType && applicationsStore.selectedReferralType !== '') {
    query.referral_type = applicationsStore.selectedReferralType
  }
  if (applicationsStore.selectedInterviewStatus && applicationsStore.selectedInterviewStatus !== '') {
    query.interview_status = applicationsStore.selectedInterviewStatus
  }
  if (selectedEmployer.value && selectedEmployer.value.id !== 'all') {
    query.employer = selectedEmployer.value.id.toString()
    query.employer_name = selectedEmployer.value.name
    applicationsStore.selectedEmployer = selectedEmployer.value.id.toString()
  } else {
    applicationsStore.selectedEmployer = ''
  }

  await router.replace({ query })
  // Reset to page 1 when filters change
  if (currentView.value === 'kanban') {
    // eslint-disable-next-line @typescript-eslint/no-floating-promises
    applicationsStore.fetchApplicationsForKanban()
  } else {
    // eslint-disable-next-line @typescript-eslint/no-floating-promises
    applicationsStore.fetchUserApplications(1)
  }
}

// eslint-disable-next-line complexity
onMounted(async () => {
  // Restore saved view preference
  const savedView = localStorage.getItem('applicationsViewMode') as 'list' | 'table' | 'kanban' | null
  if (savedView && ['list', 'table', 'kanban'].includes(savedView)) {
    currentView.value = savedView
  }

  // Initialize filter system from URL parameters first
  const currentQuery = route.query
  if (currentQuery.search && typeof currentQuery.search === 'string') {
    applicationsStore.searchQuery = currentQuery.search
  }
  if (currentQuery.application_status && typeof currentQuery.application_status === 'string') {
    applicationsStore.selectedApplicationStatus = currentQuery.application_status
  }
  if (currentQuery.referral_status && typeof currentQuery.referral_status === 'string') {
    applicationsStore.selectedReferralStatus = currentQuery.referral_status
  }
  if (currentQuery.referral_type && typeof currentQuery.referral_type === 'string') {
    applicationsStore.selectedReferralType = currentQuery.referral_type
  }
  if (currentQuery.interview_status && typeof currentQuery.interview_status === 'string') {
    applicationsStore.selectedInterviewStatus = currentQuery.interview_status
  }

  // Initialize employer search and load employers
  await employerStore.searchEmployers('')

  // Handle employer filter from URL - wait for employers to load first
  if (currentQuery.employer && typeof currentQuery.employer === 'string') {
    applicationsStore.selectedEmployer = currentQuery.employer
    const employerId = parseInt(currentQuery.employer.trim())
    const employerName = currentQuery.employer_name as string || 'Unknown Employer'
    
    if (!isNaN(employerId)) {
      // If we have both ID and name from URL, create employer object immediately
      if (employerName && employerName !== 'Unknown Employer') {
        selectedEmployer.value = { id: employerId, name: employerName }
      }
      
      // Wait for employers to load, then try to match by ID for more complete data
      await nextTick()
      const allEmployers = employerStore.employerOptions.length > 0 ? employerStore.employerOptions : employerStore.employers
      const foundEmployer = allEmployers.find(emp => emp.id === employerId)
      if (foundEmployer) {
        selectedEmployer.value = foundEmployer
      }
      // If not found but we have name from URL, keep the URL-based employer object
    }
  } else {
    // Default to 'All employers' if no employer filter in URL
    selectedEmployer.value = allEmployersOption
  }

  // Fetch applications with the restored filters
  if (currentView.value === 'kanban') {
    // eslint-disable-next-line @typescript-eslint/no-floating-promises
    applicationsStore.fetchApplicationsForKanban()
  } else {
    // eslint-disable-next-line @typescript-eslint/no-floating-promises
    applicationsStore.fetchUserApplications()
  }

  // eslint-disable-next-line @typescript-eslint/no-floating-promises
  userStore.fetchCurrentUser()
})

const handleRefreshRequested = async () => {
  if (currentView.value === 'kanban') {
    await applicationsStore.fetchApplicationsForKanban()
  } else {
    applicationsStore.lastRequestedPage = null
    await applicationsStore.fetchUserApplications()
  }
}

// Watch for employer filter changes
watch(selectedEmployer, () => {
  // eslint-disable-next-line @typescript-eslint/no-floating-promises
  updateUrlAndFetch()
}, { deep: true })

// Watch for when expanded view closes to refresh applications
watch(
  () => route.params.application_id,
  async (newAppId, oldAppId) => {
    // If we had an expanded view and now we don't, refresh the applications
    if (oldAppId && !newAppId) {
      applicationsStore.lastRequestedPage = null
      await applicationsStore.fetchUserApplications()
    }
  }
)
</script>
